import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, filter, map, pipe, switchMap, tap } from 'rxjs';

import { BranchApi } from '../data/api/branch.api';
import type { GeoPoint } from '../data/models/address/address.model';
import type { Branch } from '../data/models/branch/branch.model';
import { readStorage, writeStorage } from '../shared/utils/storage';
import { STORAGE_KEYS } from './constants';

export type BranchStatus = 'idle' | 'pending' | 'ready' | 'error';

/** 3 знака ≈ 110 м: ближайший филиал на таком сдвиге не меняется, а запрос уходил бы на каждый шаг карты. */
const CACHE_DIGITS = 3;

/** Потолок строки из localStorage: id филиала числовой. */
const STORED_FIELD_MAX = 64;

function cacheKey(point: GeoPoint): string {
  return `${point.latitude.toFixed(CACHE_DIGITS)},${point.longitude.toFixed(CACHE_DIGITS)}`;
}

/** JSON из storage недоверенный — проверяем тип и длину, а не наличие поля. */
function storedId(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 && value.length <= STORED_FIELD_MAX
    ? value
    : null;
}

/** Объект, а не голая строка: старые записи `{pickupId, deliveryId, pointKey}` читаются тем же кодом. */
function readStoredPickupId(): string | null {
  const raw = readStorage(STORAGE_KEYS.BRANCH);
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const { pickupId } = (parsed ?? {}) as Record<string, unknown>;
  return storedId(pickupId);
}

/** Читается при создании стора, а не при загрузке модуля; сверку со списком делает `loadBranches`. */
let restored: { pickupId: string | null } | null = null;

function restoreOnce(): string | null {
  return (restored ??= { pickupId: readStoredPickupId() }).pickupId;
}

type BranchState = {
  /** Ближайший к точке адреса: при доставке везёт он — филиал подбирает сервер, человек не выбирает. */
  branch: Branch | null;
  status: BranchStatus;
  branches: Branch[];
  listStatus: BranchStatus;
  /** Точка самовывоза: ручной выбор, иначе первая из списка; пересчитывается на каждой загрузке. */
  pickupId: string | null;
};

/** Root: филиал нужен и чекауту, и карточке самовывоза, а кэш обязан жить всю сессию. */
export const BranchStore = signalStore(
  { providedIn: 'root' },
  withState<BranchState>(() => ({
    branch: null,
    status: 'idle',
    branches: [],
    listStatus: 'idle',
    pickupId: restoreOnce(),
  })),

  withComputed((state) => ({
    /** Точка самовывоза: выбор из списка; ближайший — только если список не загрузился. */
    pickupBranch: computed((): Branch | null => {
      const picked = state.branches().find((branch) => branch.id === state.pickupId());
      // До ответа списка — null, а не ближайший: иначе карточка самовывоза перескакивала бы на первый.
      return picked ?? (state.listStatus() === 'error' ? state.branch() : null);
    }),
    /** Самовывоз без филиалов невозможен; до загрузки списка — fail-open, как минималка. */
    pickupAvailable: computed(
      (): boolean => state.listStatus() !== 'ready' || state.branches().length > 0,
    ),
  })),

  withMethods((store, api = inject(BranchApi)) => {
    const cache = new Map<string, Branch>();
    // Ключ последнего запроса держим вне state: наружу он не нужен, а гейт устаревших ответов — нужен.
    let requestedKey: string | null = null;
    // Точка нужна для повтора: при самовывозе сменить её нечем, а сигнал то же значение не переизлучает.
    let lastPoint: GeoPoint | null = null;
    // Расстояния держим отдельно от списка: список приходит своим запросом и порядок ответов не задан.
    let distances: ReadonlyMap<string, number> = new Map();
    let distancesKey: string | null = null;
    // Вне state: UI это не нужно, а пересортировка обязана отличать свой дефолт от выбора человека.
    let userPicked = restoreOnce() !== null;
    let defaultsToFirst = false;

    /** В storage уходит только РУЧНОЙ выбор: дефолт по списку и по расстоянию пересчитывается сам. */
    const persist = (): void => {
      const pickupId = userPicked ? store.pickupId() : null;
      writeStorage(STORAGE_KEYS.BRANCH, pickupId === null ? null : JSON.stringify({ pickupId }));
    };

    const store$ = {
      /** Точка уже выбрана вызывающим: геолокацию спрашивает форма адреса, а не филиал. */
      resolve: rxMethod<GeoPoint>(
        pipe(
          map((point) => ({ point, key: cacheKey(point) })),
          // Та же точка в сеть повторно не идёт; повтор после ошибки — только когда точка реально сменилась.
          filter(({ key }) => key !== requestedKey),
          tap(({ point, key }) => {
            requestedKey = key;
            lastPoint = point;
          }),
          switchMap(({ point, key }) => {
            const cached = cache.get(key);
            if (cached !== undefined) {
              patchState(store, { branch: cached, status: 'ready' });
              return EMPTY;
            }
            patchState(store, { branch: null, status: 'pending' });

            return api.closest(point).pipe(
              tap((branch) => {
                cache.set(key, branch);
                // switchMap отменяет только холодный источник: общий кэш или hot-поток ответ отдаст.
                if (requestedKey === key) {
                  patchState(store, { branch, status: 'ready' });
                }
              }),
              // catchError во ВНУТРЕННЕМ pipe: внешняя подписка переживает ошибку и примет новую точку.
              catchError(() => {
                if (requestedKey === key) {
                  // Ключ сбрасывается, иначе гейт выше навсегда запретил бы повтор по той же точке.
                  requestedKey = null;
                  patchState(store, { branch: null, status: 'error' });
                }
                return EMPTY;
              }),
            );
          }),
        ),
      ),

      /** Список филиалов для самовывоза; повторный вызов во время полёта запрос не плодит. */
      loadBranches: rxMethod<void>(
        pipe(
          // Список за визит не меняется: повторный вход в чекаут не должен ходить в сеть заново.
          filter(() => store.listStatus() !== 'pending' && store.listStatus() !== 'ready'),
          tap(() => patchState(store, { listStatus: 'pending' })),
          switchMap(() =>
            api.list().pipe(
              tap((list) => {
                // Расстояния могли прийти раньше списка — накладываем их сразу, а не ждём второго ответа.
                const branches = withDistances(list, distances);
                // Дефолт — первая строка того же списка, что видит человек, поэтому считается после сортировки.
                const kept = branches.some((branch) => branch.id === store.pickupId());
                const pickupId = kept ? store.pickupId() : (branches[0]?.id ?? null);
                patchState(store, { branches, listStatus: 'ready', pickupId });
                // Сохранённого филиала в ответе нет — выбор больше не наш, дефолт считается заново.
                userPicked = userPicked && kept;
                defaultsToFirst = !userPicked;
                persist();
              }),
              // catchError во ВНУТРЕННЕМ pipe: внешняя подписка переживает ошибку и примет повтор.
              catchError(() => {
                patchState(store, { listStatus: 'error' });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      /** Дополняет список расстояниями: `/nearby` молчит про филиалы без координат, а они валидные точки самовывоза. */
      loadDistances: rxMethod<GeoPoint>(
        pipe(
          map((point) => ({ point, key: cacheKey(point) })),
          // Расстояния за визит не меняются: повторный вход на страницу не должен ходить в сеть заново.
          filter(({ key }) => key !== distancesKey),
          tap(({ key }) => {
            distancesKey = key;
          }),
          switchMap(({ point }) =>
            api.nearby(point).pipe(
              tap((nearby) => {
                const next = new Map<string, number>();
                for (const branch of nearby) {
                  if (branch.distanceKm !== null && branch.distanceKm >= 0) {
                    next.set(branch.id, branch.distanceKm);
                  }
                }
                distances = next;
                const branches = withDistances(store.branches(), distances);
                // `/branch` обычно приходит первым и ставит дефолт по возрастанию id, а сортировка
                // тут его меняет: без пересчёта отмеченным осталось бы радио где-то в середине списка.
                // Явный выбор человека не трогаем — молча переставлять его нельзя.
                const pickupId = defaultsToFirst
                  ? (branches[0]?.id ?? store.pickupId())
                  : store.pickupId();
                patchState(store, { branches, pickupId });
              }),
              // catchError во ВНУТРЕННЕМ pipe: расстояния необязательны, страница живёт без них.
              catchError(() => {
                // Ключ и карту сбрасываем: иначе обрыв запретит повтор, а расстояния прошлой точки соврут про новую.
                distancesKey = null;
                distances = new Map();
                patchState(store, { branches: withDistances(store.branches(), distances) });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      /** Только точка самовывоза: филиал доставки человек не выбирает — его подбирает сервер по адресу. */
      select(id: string): void {
        userPicked = true;
        defaultsToFirst = false;
        patchState(store, { pickupId: id });
        persist();
      },

      /** Выход из сессии: точка самовывоза принадлежала прежнему человеку (рядом с cart/address reset). */
      reset(): void {
        // Список уже загружен, и `loadBranches` дефолт больше не поставит — ставим его здесь.
        userPicked = false;
        defaultsToFirst = true;
        patchState(store, { pickupId: store.branches()[0]?.id ?? null });
        persist();
      },

      /** Явный повтор той же точки: гейт по ключу иначе не пустил бы запрос после сбоя. */
      retry(): void {
        if (lastPoint !== null) {
          requestedKey = null;
          store$.resolve(lastPoint);
        }
      },
    };

    return store$;
  }),
);

/** `/nearby` возвращает только филиалы с координатами, поэтому расстояния накладываются на список по id. */
function withDistances(branches: Branch[], distances: ReadonlyMap<string, number>): Branch[] {
  return branches
    .map((branch) => ({ ...branch, distanceKm: distances.get(branch.id) ?? null }))
    .sort(byDistance);
}

/** Без координат — в конец; сортировка стабильная, поэтому при равенстве остаётся порядок `/branch` — по id. */
function byDistance(left: Branch, right: Branch): number {
  if (left.distanceKm === null) {
    return right.distanceKm === null ? 0 : 1;
  }
  if (right.distanceKm === null) {
    return -1;
  }
  return left.distanceKm - right.distanceKm;
}
